import { useEffect, useState, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { Newspaper, Send, Megaphone, RefreshCw, Loader2, CalendarClock, Star, Pin } from "lucide-react";

const TOKEN_RE = /(\*\*[^*]+\*\*|_[^_]+_|\[[^\]]+\]\([^)]+\)|<#\d*>)/g;

function DiscordText({ text }) {
  return text.split("\n").map((line, li) => (
    <div key={li} className="min-h-[1.25rem]">
      {line.split(TOKEN_RE).map((tok, i) => {
        if (tok.startsWith("**") && tok.endsWith("**")) return <strong key={i} className="text-white">{tok.slice(2, -2)}</strong>;
        if (tok.length > 2 && tok.startsWith("_") && tok.endsWith("_")) return <em key={i}>{tok.slice(1, -1)}</em>;
        const link = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link) return <a key={i} href={link[2]} target="_blank" rel="noreferrer" className="text-[#00A8FC] hover:underline">{link[1]}</a>;
        if (/^<#\d*>$/.test(tok)) return <span key={i} className="rounded bg-[#5865F2]/30 text-[#c9cdfb] px-1">#channel</span>;
        return <Fragment key={i}>{tok}</Fragment>;
      })}
    </div>
  ));
}

function DiscordMock({ data, imgUrl }) {
  return (
    <div data-testid="admin-recap-discord-mock" className="rounded-2xl bg-[#313338] p-5 text-[#dbdee1] text-sm">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-full bg-[#5865F2] flex items-center justify-center text-white font-bold text-xs">PMS</div>
        <span className="font-semibold text-white">Peak Market Society</span>
        <span className="text-[10px] font-bold bg-[#5865F2] text-white rounded px-1">BOT</span>
      </div>
      <div className="ml-11 max-w-xl rounded border-l-4 border-[#B8860B] bg-[#2b2d31] p-4 space-y-3">
        <div className="font-semibold text-white">{data.title}</div>
        <div className="space-y-3 leading-relaxed" data-testid="admin-recap-description">
          {data.description.split("\n\n").map((block, i) => <div key={i}><DiscordText text={block} /></div>)}
        </div>
        {imgUrl
          ? <img src={imgUrl} alt="Recap card" className="rounded w-full" data-testid="admin-recap-image" />
          : <div className="aspect-[1200/630] rounded bg-[#1e1f22] animate-pulse" />}
        <div className="text-[11px] text-[#949ba4]">{data.footer}</div>
      </div>
    </div>
  );
}

const Info = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
      <Icon size={12} className="text-amber-600 shrink-0" /> {label}
    </div>
    <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

export default function AdminRecap() {
  const [data, setData] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setBusy("load");
    setImgUrl(null);
    try {
      const [{ data: d }, img] = await Promise.all([
        api.get("/admin/recap/preview"),
        api.get("/admin/recap/preview.png", { responseType: "blob" }),
      ]);
      setData(d);
      setImgUrl(URL.createObjectURL(img.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed to load");
    } finally {
      setBusy("");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (kind) => {
    if (kind === "post" && !window.confirm("Post this week's recap to the Discord recap channel now? Everyone in the channel will see it.")) return;
    setBusy(kind);
    try {
      if (kind === "dm") {
        await api.post("/admin/recap/send-test-dm");
        toast.success("Sent to your Discord DMs — only you can see it");
      } else {
        const { data: r } = await api.post("/admin/recap/post-discord-now");
        r.posted ? toast.success("Recap posted to the channel") : toast.error("Post failed — check the recap channel setting");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy("");
    }
  };

  const nextRun = data && `${new Date(data.next_run_et).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET`;
  const lastRun = data?.last_run_date
    ? new Date(`${data.last_run_date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    : "—";

  return (
    <div data-testid="admin-recap-root" className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Newspaper size={26} className="text-amber-600" /> Weekly Recap
          </h1>
          <p className="text-sm text-slate-500 max-w-xl mt-1">
            Exactly what Sunday's post will look like in Discord: card, ⭐ highlights and 📌 desk notes included.
            Tip: <code className="text-xs bg-slate-100 rounded px-1">/recap-preview</code> in Discord DMs it to you too.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} disabled={!!busy} data-testid="admin-recap-refresh" className="gap-1.5">
            {busy === "load" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
          </Button>
          <Button onClick={() => run("dm")} disabled={!!busy || !data?.discord_linked} data-testid="admin-recap-send-dm"
            title={data && !data.discord_linked ? "Connect your Discord account first" : ""}
            className="gap-1.5 bg-[#5865F2] hover:bg-[#4752c4] text-white disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none">
            {busy === "dm" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test to my Discord DMs
          </Button>
          <Button variant="outline" onClick={() => run("post")} disabled={!!busy || !data?.channel_configured} data-testid="admin-recap-post-now" className="gap-1.5">
            {busy === "post" ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />} Post to channel now
          </Button>
        </div>
      </header>

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Info icon={CalendarClock} label="Next auto-post" value={nextRun} testId="admin-recap-next-run" />
          <Info icon={CalendarClock} label="Last posted" value={lastRun} testId="admin-recap-last-run" />
          <Info icon={Star} label="Highlights" value={data.highlights} testId="admin-recap-highlights-count" />
          <Info icon={Pin} label="Desk notes" value={data.desk_notes} testId="admin-recap-desk-count" />
        </div>
      )}

      {data
        ? <DiscordMock data={data} imgUrl={imgUrl} />
        : <div className="rounded-2xl bg-[#313338] h-96 animate-pulse" />}
    </div>
  );
}
